import { type ForwardRefRenderFunction, forwardRef, useEffect, useState } from 'react'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { MenuItem } from '@mui/material'
import type { SelectInputProps } from '@mui/material/Select/SelectInput'
import i18n from 'commons/libs/i18n'
import { Icon, type OptionProps } from 'components'
import dayjs from 'dayjs'

import CustomPickerModal from './components/CustomPickerModal'
import * as S from './styles'

export interface IntervalPickerProps {
  name: string
  label?: string
  value: string
  defaultValue?: string
  disabled?: boolean
  onChange?: SelectInputProps<unknown>['onChange']
  displayAsSelect?: boolean
}

// TODO: replace direct dayjs() usage with utils

const IntervalPickerComponent: ForwardRefRenderFunction<HTMLInputElement, IntervalPickerProps> = (
  { label, name, value, defaultValue, disabled, onChange, displayAsSelect },
  ref,
) => {
  const options = [
    {
      title: i18n.t('components.intervalPicker.options.open'),
      value: `${dayjs().startOf('month').format('L')} - ${dayjs().format('L')}`,
    },
    {
      title: i18n.t('components.intervalPicker.options.last3Months'),
      value: `${dayjs()
        .subtract(2, 'months')
        .startOf('month')
        .format('L')} - ${dayjs().format('L')}`,
    },
    {
      title: i18n.t('components.intervalPicker.options.last6Months'),
      value: `${dayjs()
        .subtract(5, 'months')
        .startOf('month')
        .format('L')} - ${dayjs().format('L')}`,
    },
    {
      title: i18n.t('components.intervalPicker.options.last12Months'),
      value: `${dayjs()
        .subtract(11, 'months')
        .startOf('month')
        .format('L')} - ${dayjs().format('L')}`,
    },
  ]

  const CustomText = {
    CUSTOM: i18n.t('components.intervalPicker.options.custom'),
    CUSTOM_WITH_DOTS: i18n.t('components.intervalPicker.options.custom') + '...',
  } as const

  const defaultCustomInterval = {
    title: displayAsSelect ? CustomText.CUSTOM : CustomText.CUSTOM_WITH_DOTS,
    value: 'custom',
  }

  const customIcon = displayAsSelect ? <Icon icon={'calendar-days'} size="16" /> : ''

  const [previousValue, setPreviousValue] = useState(options[3].value)
  const [customInterval, setCustomInterval] = useState<OptionProps>(defaultCustomInterval)

  const resetCustomInterval = () => {
    setCustomInterval(defaultCustomInterval)
  }

  useEffect(() => {
    if (!options.some((option) => option.value === value)) {
      const [initialDate, finalDate] = value.split(' - ')
      const startDate = dayjs(initialDate, 'DD/MM/YYYY')
      const endDate = dayjs(finalDate, 'DD/MM/YYYY')

      if (startDate.format('MM/YYYY') === endDate.format('MM/YYYY')) {
        setCustomInterval({
          title: `${startDate.format('MM/YY')}`,
          value: `${startDate.format('L')} - ${endDate.format('L')}`,
        })
      } else {
        setCustomInterval({
          title: `${startDate.format('MM/YY')} a ${endDate.format('MM/YY')}`,
          value: `${startDate.format('L')} - ${endDate.format('L')}`,
        })
      }
    }
  }, [])

  const getValueTitle = (value: string) => {
    if (options.some((option) => option.value === value)) {
      return options.find((option) => option.value === value)?.title
    } else if (customInterval.value === value) {
      return customInterval.title
    }
  }

  const renderValue = (value: string) => {
    const filtredValue = value
    return displayAsSelect ? (
      <span>{getValueTitle(filtredValue as string)}</span>
    ) : (
      <span>Mês Cota • {getValueTitle(filtredValue as string)}</span>
    )
  }

  return (
    <S.FormControlStyled displayAsSelect={displayAsSelect} fullWidth variant="outlined">
      {!!label && (
        <S.LabelStyled displayAsSelect={displayAsSelect} focused={false}>
          {label}
        </S.LabelStyled>
      )}

      <S.SelectStyled
        defaultValue={defaultValue}
        disabled={disabled}
        disableUnderline
        displayAsSelect={displayAsSelect}
        IconComponent={ExpandMoreIcon}
        inputProps={{ IconComponent: S.ChevronDownIconStyled }}
        inputRef={ref}
        MenuProps={{
          anchorOrigin: {
            horizontal: 'right',
            vertical: 'bottom',
          },
          PaperProps: {
            sx: {
              '& li': {
                fontSize: '14px',
              },
            },
          },
          transformOrigin: {
            horizontal: 'right',
            vertical: 'top',
          },
        }}
        name={name}
        onChange={onChange}
        renderValue={(value) => renderValue(value as string)}
        value={value}
        variant="standard"
      >
        {options.map((option, key) => (
          <MenuItem key={key} onClick={resetCustomInterval} value={option.value}>
            <span>{option.title}</span>
          </MenuItem>
        ))}
        <MenuItem onClick={() => setPreviousValue(value)} value={customInterval.value}>
          <S.OptionsText displayAsSelect={displayAsSelect}>
            {customInterval.title === CustomText.CUSTOM && customIcon}
            <span>{customInterval.title}</span>
          </S.OptionsText>
        </MenuItem>
        {customInterval.value !== 'custom' && (
          <MenuItem onClick={resetCustomInterval} value="custom">
            <S.OptionsText displayAsSelect={displayAsSelect}>
              {customIcon}
              <span>{defaultCustomInterval.title}</span>
            </S.OptionsText>
          </MenuItem>
        )}
      </S.SelectStyled>

      <CustomPickerModal
        fieldName={name}
        open={value === 'custom'}
        previousValue={previousValue}
        setCustomInterval={setCustomInterval}
      />
    </S.FormControlStyled>
  )
}

export const IntervalPicker = forwardRef(IntervalPickerComponent)
